import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Check } from "lucide-react";
import { apiClient } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { TEAM_ROLES } from "@/lib/reportTemplate";

const inputCls =
  "w-full rounded-md bg-[#0b1928] border border-[#243a52] focus:border-[#0ea5e9]/70 focus:outline-none px-3 py-2 text-sm text-white placeholder:text-slate-600";

export default function TeamsDialog({ open, onOpenChange, teams, onTeamsChange }) {
  const [active, setActive] = useState("A");
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const team = teams.find((t) => t.name === active);

  useEffect(() => {
    setDraft(team?.members || {});
  }, [active, team]);

  const dirty = TEAM_ROLES.some((r) => (draft[r.key] || "") !== (team?.members?.[r.key] || ""));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.put(`/reports/teams/${active}`, { members: draft });
      onTeamsChange(teams.map((t) => (t.name === active ? data : t)));
      toast.success(`Komposisi Tim ${active} disimpan`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan tim");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="teams-dialog"
        className="max-w-xl bg-[#0d243a] border-[#243a52] text-white max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users className="h-5 w-5 text-[#7dd3fc]" />
            Komposisi Tim
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Susunan nama per role untuk Tim A–E (Masinis diisi manual di form). Pilih tim di form
            laporan untuk mengisi Person in Charge otomatis. Data berlaku untuk semua user.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5" data-testid="team-tabs">
          {teams.map((t) => {
            const filled = TEAM_ROLES.filter((r) => t.members?.[r.key]).length;
            return (
              <button
                key={t.name}
                type="button"
                data-testid={`team-tab-${t.name}`}
                onClick={() => setActive(t.name)}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-display font-semibold transition-colors ${
                  active === t.name
                    ? "bg-[#0ea5e9]/20 border-[#0ea5e9]/60 text-white"
                    : "border-[#243a52] text-slate-300 hover:border-[#0ea5e9]/40"
                }`}
              >
                Tim {t.name}
                <div className="text-[9px] font-mono font-normal text-slate-500">{filled}/{TEAM_ROLES.length}</div>
              </button>
            );
          })}
        </div>

        <div className="space-y-3 mt-2" data-testid={`team-form-${active}`}>
          {TEAM_ROLES.map((r) => (
            <div key={r.key}>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-1">{r.label}</label>
              <input
                data-testid={`team-input-${active}-${r.key}`}
                value={draft[r.key] || ""}
                onChange={(e) => setDraft((d) => ({ ...d, [r.key]: e.target.value }))}
                placeholder={`Nama ${r.label} Tim ${active}`}
                className={inputCls}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            data-testid="team-save-btn"
            onClick={save}
            disabled={busy || !dirty}
            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-[#0ea5e9] text-[#0c1b2a] font-semibold hover:bg-[#38bdf8] disabled:opacity-50"
          >
            <Check className="h-4 w-4" /> Simpan Tim {active}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
